import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

/**
 * Servicio centralizado para notificaciones toast
 * Proporciona métodos para mostrar notificaciones de éxito, error, advertencia e información
 * También incluye un método para confirmaciones que reemplaza el confirm() nativo
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(private toastr: ToastrService) { }

  /**
   * Muestra una notificación de éxito
   * @param message Mensaje a mostrar
   * @param title Título opcional (por defecto: 'Éxito')
   * @param duration Duración en ms (por defecto: 3000)
   */
  success(message: string, title: string = 'Éxito', duration: number = 3000): void {
    this.toastr.success(message, title, {
      timeOut: duration,
      progressBar: true,
      closeButton: true,
      positionClass: 'toast-top-right'
    });
  }

  /**
   * Muestra una notificación de error
   * @param message Mensaje a mostrar
   * @param title Título opcional (por defecto: 'Error')
   * @param duration Duración en ms (por defecto: 5000)
   */
  error(message: string, title: string = 'Error', duration: number = 5000): void {
    this.toastr.error(message, title, {
      timeOut: duration,
      progressBar: true,
      closeButton: true,
      positionClass: 'toast-top-right',
      disableTimeOut: false
    });
  }

  /**
   * Muestra una notificación de advertencia
   * @param message Mensaje a mostrar
   * @param title Título opcional (por defecto: 'Advertencia')
   * @param duration Duración en ms (por defecto: 4000)
   */
  warning(message: string, title: string = 'Advertencia', duration: number = 4000): void {
    this.toastr.warning(message, title, {
      timeOut: duration,
      progressBar: true,
      closeButton: true,
      positionClass: 'toast-top-right'
    });
  }

  /**
   * Muestra una notificación informativa
   * @param message Mensaje a mostrar
   * @param title Título opcional (por defecto: 'Información')
   * @param duration Duración en ms (por defecto: 3000)
   */
  info(message: string, title: string = 'Información', duration: number = 3000): void {
    this.toastr.info(message, title, {
      timeOut: duration,
      progressBar: true,
      closeButton: true,
      positionClass: 'toast-top-right'
    });
  }

  /**
   * Muestra una notificación de carga/proceso
   * @param message Mensaje a mostrar
   * @param title Título opcional (por defecto: 'Procesando...')
   */
  loading(message: string, title: string = 'Procesando...'): void {
    this.toastr.info(message, title, {
      timeOut: 0, // No se cierra automáticamente
      progressBar: false,
      closeButton: false,
      positionClass: 'toast-top-right',
      disableTimeOut: true
    });
  }

  /**
   * Cierra todas las notificaciones activas
   */
  clear(): void {
    this.toastr.clear();
  }

  /**
   * Muestra un diálogo de confirmación usando SweetAlert2 (alternativa moderna a confirm())
   * Nota: Requiere instalar sweetalert2: npm install sweetalert2
   * @param message Mensaje de confirmación
   * @param onConfirm Callback a ejecutar si el usuario confirma
   * @param onCancel Callback opcional a ejecutar si el usuario cancela
   * @param title Título del diálogo (por defecto: 'Confirmar acción')
   */
  confirm(
    message: string, 
    onConfirm: () => void, 
    onCancel?: () => void,
    title: string = 'Confirmar acción'
  ): void {
    // Implementación básica con confirm nativo mejorado
    // TODO: Reemplazar con SweetAlert2 para mejor UX
    const result = window.confirm(`${title}\n\n${message}`);
    
    if (result) {
      onConfirm();
    } else if (onCancel) {
      onCancel();
    }
  }

  /**
   * Muestra una notificación personalizada
   * @param type Tipo de notificación
   * @param message Mensaje
   * @param title Título
   * @param options Opciones adicionales
   */
  show(
    type: 'success' | 'error' | 'warning' | 'info',
    message: string,
    title?: string,
    options?: any
  ): void {
    switch (type) {
      case 'success':
        this.success(message, title);
        break;
      case 'error':
        this.error(message, title);
        break;
      case 'warning':
        this.warning(message, title);
        break;
      case 'info':
        this.info(message, title);
        break;
    }
  }
}
